/**
 * GroupPanel Component
 * Config-driven Popover for nested grouping (MDF-39): up to MAX_GROUP_LEVELS
 * levels, each with an order toggle. All state is owned by the parent.
 */

import { useRef, useState } from 'react';
import {
  Popover,
  Button,
  Group,
  Text,
  Stack,
  Select,
  ActionIcon,
  Box,
  UnstyledButton,
  Divider,
  type MantineSize,
} from '@mantine/core';
import { Layers, X, Plus, ChevronDown, ArrowUp } from 'react-feather';
import { MAX_GROUP_LEVELS } from '../../../utils/groupDataByField';
import { MAX_GROUPED_ROWS } from '../../../services/api/dataLibrary';
import classes from './GroupPanel.module.css';

// ── Types ──────────────────────────────────────────────────────────────────

export interface GroupFieldConfig {
  value: string;
  label: string;
}

export interface GroupLevel {
  field: string;
  order: 'custom' | 'alphabetical';
}

/**
 * MDF-39: ordered group levels, outermost first. A plain JSON value so it can be
 * stored. Read stored values through normalizeGroupState, which also accepts the
 * old single-object shape.
 */
export type GroupState = GroupLevel[];

export interface GroupPanelProps {
  /** Available group fields */
  groupFields: GroupFieldConfig[];
  /** Currently active group levels (null = no grouping) */
  activeGroup: GroupState | null;
  /** Default group state (to distinguish "active" vs "default") */
  defaultGroup?: GroupState | null;
  /** Callback when group changes */
  onGroupChange: (group: GroupState | null) => void;
  /** Most levels the user can add (default MAX_GROUP_LEVELS) */
  maxLevels?: number;
  /** Mantine size for trigger button and inner controls (default "sm") */
  size?: MantineSize;
  /** Filtered row total when it is too large to group; shows a notice beside the button */
  limitTotal?: number | null;
  /** What the notice tells the user to do; "search" for tabs that filter in the browser (default "filter") */
  narrowBy?: 'filter' | 'search';
}

const ORDER_OPTIONS = [
  { value: 'custom', label: 'Custom order' },
  { value: 'alphabetical', label: 'Alphabetical' },
];

export function GroupPanel({
  groupFields,
  activeGroup,
  defaultGroup,
  onGroupChange,
  maxLevels = MAX_GROUP_LEVELS,
  size = 'sm',
  limitTotal,
  narrowBy = 'filter',
}: GroupPanelProps) {
  const [opened, setOpened] = useState(false);
  // The clicked button unmounts, so keep focus in the panel: Escape then closes it and Tab goes on.
  const dropdownRef = useRef<HTMLDivElement>(null);
  // The clear button unmounts too, so focus goes back to the trigger.
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [addMenuOpen, setAddMenuOpen] = useState(false);
  // Each open is a new visit: the content remounts, so no list or select stays open from the last one.
  const [visit, setVisit] = useState(0);
  const levels = activeGroup ?? [];

  // Group is "active" (non-default) when it differs from the default
  const isActive = JSON.stringify(levels) !== JSON.stringify(defaultGroup ?? []);

  const labelFor = (field: string) => groupFields.find((f) => f.value === field)?.label || field;
  const activeLabel = levels.map((l) => labelFor(l.field)).join(' > ');

  const setLevels = (next: GroupLevel[]) => onGroupChange(next.length > 0 ? next : null);

  const handleClear = () => {
    dropdownRef.current?.focus();
    onGroupChange(defaultGroup ?? null);
    setAddMenuOpen(false);
  };

  const handleDismiss = () => {
    handleClear();
    setOpened(false);
  };

  const handleFieldChange = (index: number, field: string | null) => {
    if (field) setLevels(levels.map((l, i) => (i === index ? { ...l, field } : l)));
  };

  const handleOrderChange = (index: number, order: string | null) => {
    if (order) {
      setLevels(levels.map((l, i) => (i === index ? { ...l, order: order as GroupLevel['order'] } : l)));
    }
  };

  const handleRemove = (index: number) => {
    dropdownRef.current?.focus();
    setLevels(levels.filter((_, i) => i !== index));
    setAddMenuOpen(false);
  };

  const handleMoveUp = (index: number) => {
    dropdownRef.current?.focus();
    const next = [...levels];
    [next[index - 1], next[index]] = [next[index], next[index - 1]];
    setLevels(next);
  };

  const handleSelectField = (field: string) => {
    dropdownRef.current?.focus();
    setLevels([...levels, { field, order: 'custom' }]);
    setAddMenuOpen(false);
  };

  // A field can be used at one level only
  const unusedFields = groupFields.filter((f) => !levels.some((l) => l.field === f.value));
  const canAdd = levels.length < maxLevels && unusedFields.length > 0;

  return (
    <>
    <Box pos="relative" display="inline-flex">
    <Popover
      opened={opened}
      onChange={setOpened}
      position="bottom-start"
      withinPortal
      shadow="md"
      trapFocus={false}
    >
      <Popover.Target>
        <Button
          ref={triggerRef}
          variant="outline"
          color="gray"
          bg="white"
          style={{ borderColor: '#E8E6E3' }}
          size={size}
          leftSection={<Layers size={16} />}
          // Space for the clear button, which sits beside the trigger so no button is inside a button
          rightSection={isActive ? <Box w={22} /> : undefined}
          onClick={() => {
            // The add list belongs to one visit: the panel opens with it closed.
            setAddMenuOpen(false);
            if (!opened) setVisit((v) => v + 1);
            setOpened((o) => !o);
          }}
        >
          {levels.length > 0 ? `Group: ${activeLabel}` : 'Group'}
        </Button>
      </Popover.Target>

      <Popover.Dropdown ref={dropdownRef} tabIndex={-1} className={classes.dropdown}>
        <Stack gap="sm" key={visit}>
          {/* Header */}
          <Group justify="space-between">
            <Text size="sm" fw={600}>
              Groups
            </Text>
            {levels.length > 0 && (
              <Text
                size="sm"
                c="dimmed"
                style={{ cursor: 'pointer' }}
                onClick={handleClear}
              >
                Clear
              </Text>
            )}
          </Group>

          {/* Active group rows, outermost first */}
          {levels.map((level, index) => (
            <Group key={level.field} gap="xs" wrap="nowrap">
              <Select
                data={groupFields.filter(
                  (f) => f.value === level.field || !levels.some((l) => l.field === f.value),
                )}
                value={level.field}
                onChange={(field) => handleFieldChange(index, field)}
                size={size}
                className={classes.groupFieldSelect}
                aria-label={index === 0 ? 'Group by' : 'Then group by'}
                comboboxProps={{ withinPortal: false }}
              />
              <Select
                data={ORDER_OPTIONS}
                value={level.order}
                onChange={(order) => handleOrderChange(index, order)}
                size={size}
                w={150}
                aria-label="Group order"
                comboboxProps={{ withinPortal: false }}
              />
              {maxLevels > 1 && (
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  size="md"
                  aria-label="Move level up"
                  disabled={index === 0}
                  onClick={() => handleMoveUp(index)}
                  style={index === 0 ? { visibility: 'hidden' } : undefined}
                >
                  <ArrowUp size={16} />
                </ActionIcon>
              )}
              <ActionIcon
                variant="subtle"
                color="gray"
                size="md"
                aria-label="Remove level"
                onClick={() => handleRemove(index)}
              >
                <X size={16} />
              </ActionIcon>
            </Group>
          ))}

          {/* Add a level: inline field list */}
          {canAdd &&
            (addMenuOpen ? (
              <Stack gap={0}>
                <Divider mb="xs" />
                {unusedFields.map((field) => (
                  <UnstyledButton
                    key={field.value}
                    className={classes.addOption}
                    onClick={() => handleSelectField(field.value)}
                  >
                    <Text size="sm">{field.label}</Text>
                  </UnstyledButton>
                ))}
              </Stack>
            ) : (
              <Button
                variant="subtle"
                color="gray"
                size={size}
                leftSection={<Plus size={16} />}
                rightSection={<ChevronDown size={14} />}
                onClick={() => setAddMenuOpen(true)}
              >
                {levels.length > 0 ? 'Add subgroup' : 'Add group'}
              </Button>
            ))}
        </Stack>
      </Popover.Dropdown>
    </Popover>
      {isActive && (
        <ActionIcon
          variant="transparent"
          size="sm"
          color="gray"
          aria-label="Clear grouping"
          onClick={() => {
            handleDismiss();
            triggerRef.current?.focus();
          }}
          style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)' }}
        >
          <X size={14} />
        </ActionIcon>
      )}
    </Box>
      {limitTotal != null && (
        <Text size="xs" c="dimmed">
          Grouping works on up to {MAX_GROUPED_ROWS.toLocaleString()} rows.{' '}
          {narrowBy === 'search' ? 'Search to narrow these' : 'Add a filter to group these'}{' '}
          {limitTotal.toLocaleString()} rows.
        </Text>
      )}
    </>
  );
}

export default GroupPanel;
